import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { kUntitledMatterLabel } from '@/matter/constants'
import { MattersRecord } from './slice'
import { MatterBannerKind, MatterRead, MatterReadForTable, MatterType } from './schemas'
import type { MatterMemberRead } from '@/matter/components/manage-members/schemas'
import { isActiveMatterMember } from '@/matter/utils/matter-permissions'

/**
 * Select: Matters Record
 *
 * Retrieves the full matters record from the state.
 */
export const selectMattersRecord = (state: RootState): MattersRecord => {
  return state.mattersState.matters
}

/**
 * Select: Matters List
 *
 * Returns a list of MatterRead objects, unsorted.
 */
export const selectMattersList = createSelector(selectMattersRecord, (mattersRecord): MatterRead[] => {
  return Object.values(mattersRecord).filter((matter): matter is MatterRead => !!matter)
})

/**
 * Select: Matters Sorted by Name
 *
 * Returns a list of MatterRead objects, sorted alphabetically by name (case-insensitive).
 */
export const selectMattersSortedByName = createSelector(selectMattersList, (matters): MatterRead[] => {
  return [...matters].sort((a, b) => {
    // Sort alphabetically by name, case-insensitive
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  })
})

/**
 * Select: Matter Files Destinations Sorted by Name
 *
 * Returns matters that should be offered as normal Matter Files destinations.
 * The backend remains authoritative; this is only for accepted-member UX.
 */
export const selectMatterFilesMemberMattersSortedByName = createSelector(selectMattersSortedByName, (matters): MatterRead[] => {
  return matters.filter((matter) => {
    if (matter.type !== MatterType.STANDARD && matter.type !== MatterType.SAMPLE) return false
    return isActiveMatterMember(matter)
  })
})

/**
 * Select: Matters List for Table
 *
 * Returns a list of MatterReadForTable objects with null/empty string values transformed to undefined
 * for nullable fields. This allows TanStack Table's `sortUndefined: 'last'` option to work
 * correctly, keeping blank values at the bottom regardless of sort direction.
 */
export const selectMattersListForTable = createSelector(selectMattersList, (matters): MatterReadForTable[] => {
  return matters.map((matter) => ({
    ...matter,
    // Transform null/empty strings to undefined for sortUndefined to work
    client: matter.client == null || matter.client.trim() === '' ? undefined : matter.client,
    description: matter.description == null || matter.description.trim() === '' ? undefined : matter.description,
    last_activity_at: matter.last_activity_at ?? undefined,
  }))
})

/**
 * Select: Sample Matter
 *
 * Returns the first sample matter, if one exists.
 */
export const selectSampleMatter = createSelector(selectMattersSortedByName, (matters): MatterRead | null => {
  return matters.find((matter) => matter.type === MatterType.SAMPLE) ?? null
})

/**
 * Select: Has No Non-Sample Matters
 *
 * Returns true if the user has no non-sample matters.
 */
export const selectHasNoNonSampleMatters = createSelector(selectMattersRecord, (mattersRecord) => {
  return Object.values(mattersRecord).filter((matter) => matter?.type !== MatterType.SAMPLE).length === 0
})

/**
 * Select: Matter by ID
 *
 * Returns the MatterRead object for the given matterId, or null if it does not exist.
 */
export const selectMatterById = createSelector(
  [
    selectMattersRecord,
    (state: RootState) => state.mattersState.personalWorkspace,
    (_: RootState, props: { matterId: string | undefined | null }) => props.matterId,
  ],
  (mattersRecord, personalWorkspace, matterId): MatterRead | null => {
    if (!matterId) return null
    return mattersRecord[matterId] ?? (personalWorkspace?.id === matterId ? personalWorkspace : null)
  }
)

/**
 * Select: Rendered Matter Name Map
 *
 * Returns a map of matter ids to their display names - used when we need to display
 * matter names but only have access to matter ids, without the full matter objects.
 */
export const selectRenderedMatterNameMap = createSelector([selectMattersList], (matters) => {
  const map = new Map<string, string>(matters.map((matter) => [matter.id, matter.name ?? kUntitledMatterLabel]))
  return map
})

/**
 * Select: Active Matter ID
 *
 * Retrieves the ID of the currently active matter.
 */
export const selectActiveMatterId = (state: RootState): string | null => {
  return state.mattersState.activeMatterId
}

/**
 * Select: Active Matter
 *
 * Retrieves the full MatterRead object for the currently active matter.
 * Returns null if no matter is active or the active matter is not found.
 */
export const selectActiveMatter = createSelector(
  [selectMattersRecord, selectActiveMatterId, (state: RootState) => state.mattersState.personalWorkspace],
  (mattersRecord, activeMatterId, personalWorkspace): MatterRead | null => {
    if (!activeMatterId) {
      return null
    }
    return mattersRecord[activeMatterId] ?? (personalWorkspace?.id === activeMatterId ? personalWorkspace : null)
  }
)

/**
 * Whether a matter has an accepted member other than the owner, derived from the loaded member list.
 * This is the source of truth for membership logic; `member_count` on the matter row is only for the
 * matters-table display.
 */
export function selectIsMatterShared(state: RootState, matterId: string | undefined): boolean {
  if (!matterId) return false
  const members = state.mattersState.membersByMatterId[matterId]?.members
  return members?.some((member) => member.matter_role !== 'owner' && member.accepted_at != null) ?? false
}

/* Stable reference for the empty case so useAppSelector doesn't re-render on every dispatch. */
const EMPTY_MEMBERS: MatterMemberRead[] = []

export function selectMatterMembers(state: RootState, matterId: string | undefined): MatterMemberRead[] {
  if (!matterId) return EMPTY_MEMBERS
  return state.mattersState.membersByMatterId[matterId]?.members ?? EMPTY_MEMBERS
}

export function selectMatterMembersError(state: RootState, matterId: string | undefined): boolean {
  if (!matterId) return false
  return state.mattersState.membersByMatterId[matterId]?.error ?? false
}

export function selectMatterMembersInitialLoadComplete(state: RootState, matterId: string | undefined): boolean {
  if (!matterId) return false
  return state.mattersState.membersByMatterId[matterId]?.initialLoadComplete ?? false
}

/* Stable reference for the empty case so useAppSelector doesn't re-render on every dispatch. */
const EMPTY_DISMISSED_BANNERS: MatterBannerKind[] = []

/** The setup banners the signed-in user has dismissed on one matter. */
export function selectDismissedBannerKinds(state: RootState, matterId: string | undefined): MatterBannerKind[] {
  if (!matterId) return EMPTY_DISMISSED_BANNERS
  return state.mattersState.dismissedBannersByMatterId[matterId] ?? EMPTY_DISMISSED_BANNERS
}

/**
 * Whether this matter's dismissal set is known yet, so a caller can tell "nothing dismissed" apart
 * from "not loaded yet" and avoid flashing a banner the user already cleared.
 */
export function selectDismissedBannersLoaded(state: RootState, matterId: string | undefined): boolean {
  if (!matterId) return false
  return state.mattersState.dismissedBannersByMatterId[matterId] !== undefined
}
